import {
  AccountProductEnum,
  InternalAdvanceAccount,
  MyAccounts,
  Query,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import { IAccountTypes } from 'features/accounts/hooks/useAccountDetails';
import useVaroAdvanceProducts from 'features/advance/hooks/useGetVaroAdvanceProducts';
import { useEffect, useState } from 'react';
import AccountIds from 'services/graphql/queries/AccountIds';
import GetAccountR2 from 'services/graphql/queries/AccountR2';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { useQuery } from 'urql';

const useAdvanceStatus = () => {
  const [accountIds, setAccountIds] = useState<{
    checkingAccountId: string | null;
    advanceAccountId: string | null;
    paramId: string | null;
  }>({
    checkingAccountId: null,
    advanceAccountId: null,
    paramId: null,
  });

  const { id } = useLocalSearchParams<{ id: string }>();
  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const [{ data: myAccountsData, fetching, error: myAccountsError }] = useQuery<
    Query,
    MyAccounts
  >({
    query: AccountIds,
    requestPolicy: 'network-only',
    pause: !!id,
  });

  const {
    data: advanceProductsData,
    fetching: advanceProductsFetching,
    error: advanceProductsError,
  } = useVaroAdvanceProducts('network-only');

  useEffect(() => {
    if (id) {
      setAccountIds((prevValues) => ({
        ...prevValues,
        paramId: id?.length ? id : null,
      }));
    }
    if (!fetching && !!myAccountsData) {
      const accounts =
        myAccountsData?.myAccounts?.internalV2.map((account) => ({
          id: account.id,
          product: account.product,
        })) || [];

      const checkingAccountId = accounts.find(
        (account) => account.product === AccountProductEnum.Checking,
      )?.id;

      const advanceAccountId = accounts.find(
        (account) => account.product === AccountProductEnum.Advance,
      )?.id;

      setAccountIds((prevValues) => ({
        ...prevValues,
        checkingAccountId: checkingAccountId ?? null,
        advanceAccountId: advanceAccountId ?? null,
      }));
    }
  }, [fetching, id, myAccountsData]);

  const [
    { data: accountR2, fetching: fetchingAccount, error: accountError },
    executeQuery,
  ] = useQuery<IAccountTypes>({
    query: GetAccountR2,
    requestPolicy: 'network-only',
    variables: {
      id:
        accountIds.paramId ??
        accountIds.advanceAccountId ??
        accountIds.checkingAccountId,
    },
    pause: true,
  });

  const advanceAccount =
    accountR2?.accountR2?.product === AccountProductEnum.Advance
      ? (accountR2.accountR2 as InternalAdvanceAccount)
      : null;

  const checkingAccount =
    accountR2?.accountR2?.product === AccountProductEnum.Checking
      ? accountR2.accountR2
      : null;

  useEffect(() => {
    const id =
      accountIds.paramId ??
      accountIds.advanceAccountId ??
      accountIds.checkingAccountId;

    if (id && !fetchingAccount && !accountR2 && !accountError) {
      executeQuery({ id: id });
    }
  }, [
    accountError,
    accountIds.advanceAccountId,
    accountIds.checkingAccountId,
    accountIds.paramId,
    accountR2,
    advanceAccount,
    executeQuery,
    fetchingAccount,
  ]);

  const { defaultTitle, defaultMessage, cancelButtonTitle } =
    useDefaultErrorStrings();
  const buttons = {
    primaryButton: {
      title: cancelButtonTitle,
      onPress: () => {
        goHome();
      },
    },
  };
  const accountErrorModel = accountError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: accountError?.name ?? defaultTitle,
        messageBody: accountError.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const myAccountsErrorModel = myAccountsError
    ? {
        screenType: ErrorScreenType.Error,
        messageHeadline: myAccountsError?.name ?? defaultTitle,
        messageBody: myAccountsError.message ?? defaultMessage,
        ...buttons,
      }
    : null;

  const isFetching = fetching || fetchingAccount || advanceProductsFetching;
  const error =
    myAccountsErrorModel ?? accountErrorModel ?? advanceProductsError;

  if (isFetching) {
    return {
      fetching: true,
      error: null,
      data: {
        advanceProductsData: null,
        advanceAccount: null,
        checkingAccount: null,
        accountData: null,
        isFirstTime: null,
        checkingAccountId: accountIds.checkingAccountId,
        advanceAccountId: accountIds.advanceAccountId,
      },
    };
  }
  return {
    fetching: isFetching,
    error: error,
    data: {
      advanceProductsData,
      advanceAccount,
      checkingAccount,
      accountR2,
    },
  };
};

export default useAdvanceStatus;
